/**
 * SessionWarningModal Component
 * HIPAA Compliance: Warns user before automatic session timeout
 * Regulation: 45 CFR 164.312(a)(2)(iii) - Automatic Logoff
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const SessionWarningModal = ({
  open,
  remainingSeconds,
  onExtendSession,
  onLogout
}) => {
  const totalWarningSeconds = 120; // 2 minutes warning
  const progress = (remainingSeconds / totalWarningSeconds) * 100;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderTop: '4px solid #ed6c02'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        <Typography variant="h6" component="span">
          Session Expiring Soon
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          For security purposes, your session will expire due to inactivity.
        </Typography>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography
            variant="h3"
            align="center"
            color={remainingSeconds <= 30 ? 'error' : 'warning.main'}
            sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
          >
            {formatTime(remainingSeconds)}
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary">
            Time remaining
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          color={remainingSeconds <= 30 ? 'error' : 'warning'}
          sx={{ height: 8, borderRadius: 4, mb: 2 }}
        />

        <Typography variant="body2" color="text.secondary">
          Click "Continue Working" to extend your session, or you will be automatically logged out.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onLogout}
          color="inherit"
        >
          Logout Now
        </Button>
        <Button
          onClick={onExtendSession}
          variant="contained"
          color="primary"
          autoFocus
        >
          Continue Working
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionWarningModal;
